import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { CSSProperties } from 'react';
import '../../index.scss';
import drag from '../../../../../../../assets/drag.png';
import arrow from '../../../../../../../assets/arrow.svg';
import InfoIcon from '../../../../../../../components/Icons/infoIcon';
import { FormItem } from '../../../../../../../api/types/expenseareas';
import { getSpqBackendMessagesForField } from '../spqBackendValidation';

/** Только вёрстка поля — без useSortable (для DragOverlay, иначе дублируется id в контексте сортировки). */
const FormItemCanvasInner = ({
    item,
    isRequired,
    withDragHandleIcon
}: {
    item: FormItem;
    isRequired: boolean;
    withDragHandleIcon?: boolean;
}) => {
    return (
        <>
            {(item?.type === 'short-answer' || item?.type === 'paragraph') && (
                <>
                    {withDragHandleIcon && <img src={drag} alt="" className="drag-overlay__icon" />}
                    <div>
                        <div className="short-answer">
                            {item?.label ? item?.label : 'Label name'}{' '}
                            {isRequired ? <div className="required">*</div> : ''}
                        </div>
                        {item?.description && <div className="helper-text">{item?.description}</div>}
                    </div>
                </>
            )}

            {item?.type === 'single-select' && (
                <>
                    {withDragHandleIcon && <img src={drag} alt="" className="drag-overlay__icon" />}
                    <div className="single-select">
                        <div className="row">
                            {item?.label ? item?.label : 'Label name'}
                            {isRequired ? <div className="required">*</div> : ''}
                        </div>{' '}
                        {item?.description && <div className="helper-text">{item?.description}</div>}
                        <div className="single-select__wrapper">
                            {item?.options?.map((elem, index) => {
                                return (
                                    <div className="single-select__option" key={index}>
                                        <div className="single-select__radio"></div>{' '}
                                        {elem?.value ? elem.value : `Option ${index + 1}`}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}
            {item?.type === 'multiple-choice' && (
                <>
                    {withDragHandleIcon && <img src={drag} alt="" className="drag-overlay__icon" />}
                    <div className="single-select">
                        <div className="row">
                            {' '}
                            {item?.label ? item?.label : 'Label name'}{' '}
                            {isRequired ? <div className="required">*</div> : ''}
                        </div>

                        {item?.description && <div className="helper-text">{item?.description}</div>}

                        <div className="single-select__wrapper">
                            {item?.options?.map((elem, index) => {
                                return (
                                    <div className="single-select__option" key={index}>
                                        <div className="single-select__checkbox"></div>{' '}
                                        {elem?.value ? elem.value : `Option ${index + 1}`}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}
            {item?.type === 'dropdown' && (
                <>
                    {withDragHandleIcon && <img src={drag} alt="" className="drag-overlay__icon" />}
                    <div className="dropdown">
                        <div className="row">
                            {item?.label ? item?.label : 'Label name'}
                            {isRequired ? <div className="required">*</div> : ''}
                        </div>
                        <img src={arrow} alt="" />
                    </div>
                    {item?.description && <div className="helper-text">{item?.description}</div>}
                </>
            )}
            {item?.type === 'explanation-banner' && (
                <>
                    {withDragHandleIcon && <img src={drag} alt="" className="drag-overlay__icon" />}
                    <div className="explanation-banner" style={{ whiteSpace: 'pre-wrap' }}>
                        <InfoIcon fill="#001965" />
                        {item?.description ? item?.description : 'Please, enter explanation'}
                    </div>
                </>
            )}
        </>
    );
};

/** Превью в DragOverlay — не регистрируется в sortable. */
export const SortableFormItemDragOverlay = ({ item }: { item: FormItem }) => {
    const isRequired = item?.settings?.find((elem) => elem.type === 'required')?.switchValue;

    return (
        <div className="draggable drag-overlay" style={{ cursor: 'grabbing' }}>
            <FormItemCanvasInner item={item} isRequired={!!isRequired} withDragHandleIcon />
        </div>
    );
};

export const SortableFormItem = ({
    id,
    item,
    selectedElement,
    fieldsError,
    backendSpqErrors,
    onSelect
}: {
    id: string;
    item: FormItem;
    selectedElement: null | string;
    fieldsError: string[];
    backendSpqErrors: Record<string, string[]>;
    onSelect?: (id: string) => void;
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    const style: CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : undefined,
        touchAction: 'none'
    };

    const isRequired = item?.settings?.find((elem) => elem.type === 'required')?.switchValue;

    const fieldHasError = fieldsError.some((fid) => String(fid) === String(id));
    const backendFieldMessages = getSpqBackendMessagesForField(backendSpqErrors, id);
    const fieldHasBackendError = backendFieldMessages.length > 0;

    return (
        <>
            <div
                ref={setNodeRef}
                {...attributes}
                {...listeners}
                role="presentation"
                onClick={() => onSelect?.(String(item.id))}
                style={style}
                data-spq-field-id={String(id)}
                className={`draggable ${selectedElement === item.id + '' ? 'selected' : ''} ${
                    fieldHasError || fieldHasBackendError ? 'error' : ''
                }`}
                key={item.id}
            >
                <FormItemCanvasInner item={item} isRequired={!!isRequired} />
            </div>
            {fieldHasError && !isDragging && (
                <div className="error-text">This field is not set yet. Please set field</div>
            )}
            {fieldHasBackendError && !isDragging && (
                <>
                    {backendFieldMessages.map((msg, i) => (
                        <div key={`be-${i}`} className="error-text">
                            {msg}
                        </div>
                    ))}
                </>
            )}
        </>
    );
};
